import { dlog, dLogTime, dLogTimeEnd } from "./utility.js";
import { debugDesc } from "./utility.js";
import { notificationCenter } from "@shared/NotificationCenter.js";
import { currentDatabaseName, getCurrentServerEnv, ServerEnv } from "@shared/environment.js";
import { initializeApp } from "firebase/app";
import { getAuth, signInAnonymously } from "firebase/auth";
import {
    getFirestore,
    connectFirestoreEmulator,
    doc,
    getDoc as getDocOriginal,
    getDocs as getDocsOriginal,
    updateDoc,
    collection,
    increment,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics, logEvent as firebaseLogEvent } from "firebase/analytics";

class FirebaseManager {
    static authStateDidChangeNotification = "firebaseAuthStateChanged";

    constructor() {
        dlog(">>> FirebaseManager");

        this.firebaseConfig = {
            apiKey: "AIzaSyC7vYFfbDh8L4MbyqNt6pscA4L6D48o-w8",
            authDomain: "ai-gallery-60cd7.firebaseapp.com",
            projectId: "ai-gallery-60cd7",
            storageBucket: "ai-gallery-60cd7.appspot.com",
            messagingSenderId: "546322937580",
            appId: "1:546322937580:web:80750e98e853ac5e69d7c9",
            measurementId: "G-116442XREW"
        };
        this.app = initializeApp(this.firebaseConfig);
        this.auth = getAuth();
        this.auth.useDeviceLanguage();
        this.db = this.initializeDatabase();
        this.storage = getStorage();
        this.functions = getFunctions();
        this.itemCntIncs = new Map();

        // Initialize analytics only in production
        this.analytics = getCurrentServerEnv() === ServerEnv.prod ? getAnalytics(this.app) : null;

        // Cache for signed URLs with configurable expiration
        this.signedURLCache = new Map();
        this.cacheExpirationMinutes = 60; // Default to 60 minutes, same as signed URL expiration

        // Firestore read counter
        this.firestoreReadCount = 0;
        
        this.auth.onAuthStateChanged(user => {
            dlog("Auth state changed; user:" + debugDesc(user));
            if (user) {
                notificationCenter.post(FirebaseManager.authStateDidChangeNotification, user);
            } else {
                // will retrigger this listener
                this.signInToFirebaseAnonymously();
            }
        });
    }
    
    initializeDatabase() {
        const dbName = currentDatabaseName();
        dlog(`Initializing database: ${dbName}`);
        
        if (dbName === "(default)") {
            return getFirestore(this.app);
        } else {
            return getFirestore(this.app, dbName);
        }
    }

    async incItemViewCnt(feedItem, collectionName) {
        if (this.itemCntIncs.get(feedItem.id) != null) {
            dlog("suppressing incItemViewCnt on " + feedItem.id);
            return;
        }
        try {
            dlog("incItemViewCnt start");

            await this.withRetry(async () => {
                const docRef = doc(this.db, collectionName, feedItem.id);
                const docSnap = await getDocOriginal(docRef);
                if (!docSnap.exists()) {
                    throw new Error("No such Firestore document.");
                }
                // Track the read
                this.incrementReadCount(1);
                await updateDoc(docRef, { view_cnt: increment(1) });
            });

            this.itemCntIncs.set(feedItem.id, true);
            feedItem.view_cnt = !feedItem.view_cnt ? 1 : feedItem.view_cnt + 1;
            dlog("incItemViewCnt done");
        } catch (error) {
            console.error("Error incrementing view count after retries: ", error);
        }
    }
    
    async signInToFirebaseAnonymously() {
        dLogTime("signInToFirebaseAnonymously");
        dlog("signInToFirebaseAnonymously");
        
        try {
            await signInAnonymously(this.auth);
            dlog("User signed in anonymously");
            dLogTimeEnd("signInToFirebaseAnonymously");
        } catch (error) {
            var errorCode = error.code;
            var errorMessage = error.message;
            console.error("Error during anonymous sign-in:", errorCode, errorMessage);
            throw error; // Re-throw so the retry logic can handle it
        }
    }
    
    // Configure cache expiration time (in minutes)
    setCacheExpirationMinutes(minutes) {
        this.cacheExpirationMinutes = minutes;
    }

    // Clear the entire cache
    clearSignedURLCache() {
        this.signedURLCache.clear();
    }

    // Clear a specific URL from cache
    clearCachedURL(path) {
        this.signedURLCache.delete(path);
    }
    
    async withRetry(operation, maxAttempts = 10) {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                return await operation();
            } catch (error) {
                dlog(`Firebase operation failed (attempt ${attempt}/${maxAttempts}): ${error.message}`);

                if (attempt === maxAttempts) {
                    console.warn(`Firebase operation failed after ${maxAttempts} attempts:`, error);
                    throw error;
                }

                dlog("Attempting re-authentication...");
                try {
                    await this.signInToFirebaseAnonymously();
                } catch (authError) {
                    console.warn("Re-authentication failed:", authError);
                }

                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    }

    // Firestore read tracking methods
    incrementReadCount(count = 1) {
        this.firestoreReadCount += count;
        dlog(`Firestore read count: ${this.firestoreReadCount}`);
    }

    getReadCount() {
        return this.firestoreReadCount;
    }

    resetReadCount() {
        this.firestoreReadCount = 0;
        console.log("Firestore read count reset to 0");
    }

    printReadCount() {
        console.log(`Total Firestore reads: ${this.firestoreReadCount}`);
        return this.firestoreReadCount;
    }

    logEvent(eventName, params = null) {
        if (this.analytics) {
            if (params) {
                firebaseLogEvent(this.analytics, eventName, params);
            } else {
                firebaseLogEvent(this.analytics, eventName);
            }
        }
    }

}

const firebaseShared = new FirebaseManager();

// Wrapper for getDoc to track reads
async function getDocTracked(docRef) {
    const result = await getDocOriginal(docRef);
    if (result.exists()) {
        firebaseShared.incrementReadCount(1);
    }
    return result;
}

// Wrapper for getDocs to track reads
async function getDocsTracked(collectionRef) {
    const result = await getDocsOriginal(collectionRef);
    const readCount = result.size;
    firebaseShared.incrementReadCount(readCount);
    return result;
}

// Make the counter globally accessible for debugging
if (typeof window !== "undefined") {
    window.printFirestoreReads = () => firebaseShared.printReadCount();
    window.resetFirestoreReads = () => firebaseShared.resetReadCount();
}

function logEvent(eventName, params = null) {
    firebaseShared.logEvent(eventName, params);
}

export {
    FirebaseManager,
    firebaseShared,
    signInAnonymously,
    doc,
    getDocTracked as getDoc,
    getDocsTracked as getDocs,
    collection,
    ref,
    logEvent
};

